<script setup lang="ts">
// Vue
import { computed, watch } from "vue";
import { Boxes, SlidersHorizontal, Waypoints } from "@lucide/vue";

// Providers
import { LocalModelType, useCreateBuildingProject, useModels } from "@/features/BuildingProject";
import { useDefaultModels } from "@/dataProviders/DefaultModels";

// Components
import Divider from "@/components/Reusables/Divider/Divider.vue";
import CustomModels from "./CustomModels.vue";
import NonSelectedModels from "./NonSelectedModels.vue";
import SelectedModels from "./SelectedModels.vue";
import ParameterMapping from "./../../ParameterMapping";

const models = useModels();
const defaultModels = useDefaultModels();
const createBuildingProject = useCreateBuildingProject();

watch(
  () => defaultModels.getDefaultModels(),
  () => {
    models.setDefaultModels();
  },
  { immediate: true }
);

const activeModels = computed(() => models.getActiveModels());
const inactiveModels = computed(() => models.getInactiveModels());
const modelNames = computed(() => models.getModels().map((model) => model.name));

// The store owns the list; these forward the edit to it.
const toggleModel = (model: LocalModelType) => models.toggleModel(model);
const handleAddNewModel = (model: LocalModelType) => models.addModel(model);
</script>

<template>
  <!-- The configuration view for choice 1, on the same card grammar as every other step. The first
       card's header names the choice made one click ago, since the step heading above still asks the
       question. The old InfoIcon tooltips became each card's caption line: the same words, readable
       without hovering anything. -->
  <div class="flex w-full flex-col gap-3">
    <!-- What is Speckle -->
    <div class="rounded-8 bg-slate-0 p-px-20 flex flex-col gap-3 border border-slate-300">
      <div
        class="-mx-px-20 -mt-px-20 px-px-20 pt-px-20 rounded-t-8 flex flex-col gap-1 border-b border-slate-300 bg-slate-50 pb-3">
        <div class="flex items-center gap-2">
          <span class="bg-app-blue-50 rounded-4 flex shrink-0 items-center justify-center p-1">
            <Waypoints
              class="icon-sm text-app-blue-600"
              :stroke-width="1.5"
              aria-hidden="true" />
          </span>
          <h2 class="t-sh4 text-type-default">A new Speckle connection</h2>
        </div>
      </div>
      <p class="t-small text-type-default max-w-prose">
        A new Speckle connection is the bridge to your 3D model environment. It lets you send your 3D model to the platform, where
        you map EPDs or materials from our database onto the materials in the model.
      </p>
      <p class="t-small text-type-default max-w-prose">
        When the 3D model changes, you simply send it again and we update the emission calculation accordingly.
      </p>
    </div>

    <!-- Model configuration -->
    <div class="rounded-8 bg-slate-0 p-px-20 flex flex-col gap-3 border border-slate-300">
      <div
        class="-mx-px-20 -mt-px-20 px-px-20 pt-px-20 rounded-t-8 flex flex-col gap-1 border-b border-slate-300 bg-slate-50 pb-3">
        <div class="flex items-center gap-2">
          <span class="bg-app-blue-50 rounded-4 flex shrink-0 items-center justify-center p-1">
            <Boxes
              class="icon-sm text-app-blue-600"
              :stroke-width="1.5"
              aria-hidden="true" />
          </span>
          <h2 class="t-sh4 text-type-default">Model configuration</h2>
        </div>
        <p class="t-caption text-type-muted max-w-prose">Turn on the models to include in this project.</p>
      </div>
      <SelectedModels
        :models="activeModels"
        @toggle-model="toggleModel" />
      <NonSelectedModels
        :models="inactiveModels"
        @toggle-model="toggleModel" />
      <Divider spacing="sm" />
      <CustomModels
        :existing-model-names="modelNames"
        @add-new-model="handleAddNewModel" />
    </div>

    <!-- Parameter mapping -->
    <div class="rounded-8 bg-slate-0 p-px-20 flex flex-col gap-3 border border-slate-300">
      <div
        class="-mx-px-20 -mt-px-20 px-px-20 pt-px-20 rounded-t-8 flex flex-col gap-1 border-b border-slate-300 bg-slate-50 pb-3">
        <div class="flex items-center gap-2">
          <span class="bg-app-blue-50 rounded-4 flex shrink-0 items-center justify-center p-1">
            <SlidersHorizontal
              class="icon-sm text-app-blue-600"
              :stroke-width="1.5"
              aria-hidden="true" />
          </span>
          <h2 class="t-sh4 text-type-default">Parameter mapping</h2>
        </div>
        <p class="t-caption text-type-muted max-w-prose">
          Choose the parameter where your authoring tool stores the classification code. It is read every time a new version
          arrives through Speckle, and the code is what structures the results.
        </p>
      </div>
      <ParameterMapping :project="createBuildingProject.getProject()" />
    </div>
  </div>
</template>
